import { useEffect, useState } from "react";
import { toast } from "sonner";
import { BellRinging, X } from "@phosphor-icons/react";
import { Button } from "./ui/button";
import {
  pushSupportedInBrowser,
  getSubscription,
  ensureSubscribed,
} from "../lib/pushClient";

const DISMISS_KEY = "tain-push-dismissed-until";

/**
 * PushOptInBanner — a lightweight bottom-of-screen prompt that asks the user
 * once (per week if dismissed) to enable notifications. Shown to authenticated
 * users on any screen that mounts this component. Silently no-ops when the
 * browser doesn't support push or the user has already granted / permanently
 * denied permission.
 */
export function PushOptInBanner({ user }) {
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!user) return;
      if (!pushSupportedInBrowser) return;
      // Permanent OS-level deny → nothing we can do.
      if (Notification.permission === "denied") return;
      // Already subscribed on this device? Silently re-sync with backend
      // (so a re-login on a new tab still marks the device as active).
      if (Notification.permission === "granted") {
        try {
          const sub = await getSubscription();
          if (sub) {
            await ensureSubscribed();
            return;
          }
        } catch { /* ignore */ }
      }
      // User dismissed recently?
      const until = Number(localStorage.getItem(DISMISS_KEY) || 0);
      if (until && until > Date.now()) return;
      if (!cancelled) setShow(true);
    })();
    return () => { cancelled = true; };
  }, [user]);

  const enable = async () => {
    setBusy(true);
    const r = await ensureSubscribed();
    setBusy(false);
    if (r.ok) {
      toast.success("Notifications enabled — we'll ping you when something new lands");
      setShow(false);
    } else if (r.permission === "denied") {
      toast.error("Notification permission was blocked. Enable it in your browser settings.");
      setShow(false);
    } else {
      toast.error(`Couldn't enable notifications (${r.reason || "unknown"})`);
    }
  };

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, String(Date.now() + 7 * 24 * 60 * 60 * 1000));
    setShow(false);
  };

  if (!show) return null;
  return (
    <div
      data-testid="push-opt-in-banner"
      className="fixed left-1/2 -translate-x-1/2 z-40 w-[92%] max-w-md
                 bottom-[calc(env(safe-area-inset-bottom,0px)+92px)] md:bottom-6"
    >
      <div className="rounded-2xl bg-[#0f0f0f] border border-white/10
                      shadow-[0_20px_60px_rgba(0,0,0,0.55)] p-4 flex items-start gap-3">
        <div className="shrink-0 w-10 h-10 rounded-full bg-fuchsia-500/15 border border-fuchsia-500/40
                        flex items-center justify-center">
          <BellRinging size={20} weight="fill" className="text-fuchsia-300" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm text-white font-semibold">Turn on notifications</p>
          <p className="text-[12px] text-white/60 mt-0.5 leading-relaxed">
            Get instant push alerts for messages, jobs, partnerships, school updates and Credits —
            same as our emails, straight to your device.
          </p>
          <div className="flex gap-2 mt-3">
            <Button
              data-testid="push-enable-btn"
              size="sm"
              disabled={busy}
              onClick={enable}
              className="bg-fuchsia-500 hover:bg-fuchsia-600 text-black font-bold h-8 text-xs"
            >
              {busy ? "Enabling…" : "Enable"}
            </Button>
            <Button
              data-testid="push-dismiss-btn"
              variant="outline"
              size="sm"
              onClick={dismiss}
              className="border-white/15 bg-transparent hover:bg-white/5 text-white h-8 text-xs"
            >
              Not now
            </Button>
          </div>
        </div>
        <button
          onClick={dismiss}
          data-testid="push-close"
          className="text-white/40 hover:text-white/70 p-1 -mr-1"
          aria-label="Close"
        >
          <X size={16} />
        </button>
      </div>
    </div>
  );
}

export default PushOptInBanner;
